import { useState, useEffect, forwardRef } from 'react';
import { Space, Button, Select } from 'antd';
import { useBooleanState } from './commonHooks';

const { Option } = Select;

const DynamicSelect = forwardRef(
  (
    {
      valueLoader,
      defaultValueSelector,
      optionRenderer = (option) => option.label,
      addHandler,
      onChange,
      value,
      loadingText = 'Loading...',
      errorText = 'Error loading options',
      selectText = 'Select an option',
      noOptionsText = 'No options available',
      addText = 'Add',
      ...rest
    },
    ref,
  ) => {
    const loading = useBooleanState(true);
    const [options, setOptions] = useState([]);
    const [error, setError] = useState(null);
    const [selectedValue, setSelectedValue] = useState(value);

    useEffect(() => {
      const fetchOptions = async () => {
        try {
          const loadedOptions = await valueLoader();
          setOptions(loadedOptions);

          if (defaultValueSelector && !selectedValue) {
            const defaultValue = defaultValueSelector(loadedOptions);
            if (defaultValue) {
              setSelectedValue(defaultValue);
              if (onChange) {
                onChange(defaultValue);
              }
            }
          }
        } catch (err) {
          setError('Error loading options');
        } finally {
          loading.setFalse();
        }
      };

      fetchOptions();
    }, [valueLoader, defaultValueSelector, onChange, selectedValue, loading]);

    const handleChange = (newValue) => {
      setSelectedValue(newValue);
      if (onChange) {
        onChange(newValue);
      }
    };

    const handleAddItems = async () => {
      loading.setTrue();
      await addHandler();
      const loadedOptions = await valueLoader();
      setOptions(loadedOptions);
      loading.setFalse();
    };

    return (
      <Space>
        {(options.length > 0 || loading.value || !addHandler) && (
          <Select
            {...rest}
            value={selectedValue}
            loading={loading.value}
            filterOption={false}
            onChange={handleChange}
            placeholder={
              loading.value ? loadingText : error ? errorText : options.length === 0 ? noOptionsText : selectText
            }
            disabled={options.length === 0}
            ref={ref}
          >
            {options.length > 0 &&
              options.map((optionData, index) => (
                <Option key={index} value={optionData.value}>
                  {optionRenderer(optionData)}
                </Option>
              ))}
          </Select>
        )}

        {addHandler && !loading.value && (
          <Button disabled={loading.value} onClick={handleAddItems}>
            {addText}
          </Button>
        )}
      </Space>
    );
  },
);

export default DynamicSelect;
